<template>
  <div class="bg-fixed join-class-page">
    <div class="join-class-container scrollable">
      <!-- 步驟1：驗證邀請碼 -->
      <div v-if="step === 1" class="code-entry">
        <h2>{{ $t("enter-invitation-code") }}</h2>
        <input
          v-model="inviteCode"
          :placeholder="$t('please-enter-invitation-code')"
        />
        <button @click="verifyCode">{{ $t("confirm") }}</button>
        <p v-if="error" class="error">{{ error }}</p>
      </div>

      <!-- 步驟2：選擇操作類型 -->
      <div v-if="step === 2" class="action-selection">
        <h2>{{ $t("please-select-action") }}</h2>
        <div class="action-buttons">
          <button @click="selectAction('register')" class="action-btn">
            <div class="action-icon">👤</div>
            <div class="action-title">{{ $t("new-user-registration") }}</div>
            <div class="action-desc">{{ $t("register-new-account-desc") }}</div>
          </button>
          <button @click="selectAction('subscription')" class="action-btn">
            <div class="action-icon">⏰</div>
            <div class="action-title">{{ $t("subscription-extension") }}</div>
            <div class="action-desc">
              {{ $t("extend-existing-subscription-desc") }}
            </div>
          </button>
        </div>
      </div>

      <!-- 步驟3：新用戶註冊表單 -->
      <div
        v-if="step === 3 && activeTab === 'register'"
        class="registration-form form-with-back"
      >
        <button @click="goBack" class="back-button" type="button">
          ← {{ $t("back") }}
        </button>
        <h2 class="form-title-with-back">
          {{ $t("please-fill-in-your-information") }}
        </h2>
        <form @submit.prevent="registerUser">
          <div class="form-group">
            <label>{{ $t("username") }} <span class="required">*</span></label>
            <input v-model="userData.username" required />
          </div>
          <div class="form-group">
            <label>{{ $t("password") }} <span class="required">*</span></label>
            <input v-model="userData.uni_info" type="password" required />
          </div>
          <div class="form-group">
            <label
              >{{ $t("confirm-password") }}
              <span class="required">*</span></label
            >
            <input v-model="passwordConfirm" type="password" required />
          </div>

          <div class="form-group">
            <label
              >{{ $t("set-graphic-password") }}
              <span class="required">*</span></label
            >
            <PatternPassword
              v-model="userData.patternPw"
              :emojis="emojiOrder"
            />
          </div>
          <p v-if="error" class="error">{{ error }}</p>
          <button type="submit">{{ $t("complete-registration") }}</button>
        </form>
      </div>
      <!-- 步驟4：註冊成功訊息 -->
      <div
        v-if="step === 4 && activeTab === 'register'"
        class="success-message"
      >
        <h2>{{ $t("registration-successful") }}</h2>
        <p>{{ $t("you-have-successfully-joined-the-class") }}</p>
        <!-- <p>
          {{ $t("your-student-id-is") }}: {{ registrationResult.studentId }}
        </p> -->
        <button @click="goToLogin">{{ $t("go-to-login") }}</button>
      </div>

      <!-- 步驟3：訂閱展延表單 -->
      <div
        v-if="step === 3 && activeTab === 'subscription'"
        class="subscription-form form-with-back"
      >
        <!-- 訂閱表單 -->
        <div v-if="!subscriptionSuccess">
          <button
            @click="goBack"
            class="back-button text-bold btn-md"
            type="button"
          >
            ← {{ $t("back") }}
          </button>
          <h2 class="form-title-with-back">
            {{ $t("subscription-extension") }}
          </h2>
          <form @submit.prevent="submitSubscription">
            <div class="form-group">
              <label
                >{{ $t("username") }} <span class="required">*</span></label
              >
              <input v-model="subscriptionData.username" required />
            </div>

            <div class="form-group">
              <label
                >{{ $t("pin-code") }} <span class="required">*</span></label
              >
              <input
                v-model="subscriptionData.uniInfo"
                type="password"
                required
              />
            </div>

            <div class="form-group">
              <label>{{ $t("invitation-code") }}</label>
              <input
                v-model="subscriptionData.inviteCode"
                readonly
                :placeholder="$t('auto-filled-from-url')"
              />
            </div>

            <p v-if="subscriptionError" class="error">
              {{ subscriptionError }}
            </p>
            <button type="submit">{{ $t("submit") }}</button>
          </form>
        </div>

        <!-- 訂閱成功訊息 -->
        <div v-if="subscriptionSuccess" class="success-message">
          <h2>{{ $t("subscription-successful") }}</h2>
          <p>{{ $t("subscription-updated-successfully") }}</p>
          <button @click="goToLogin">{{ $t("go-to-login") }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import PatternPassword from "@/components/PatternPassword.vue";
import {
  ApiValidateInvitationCode,
  ApiRegisterAndJoinClass,
  ApiAddStudentSubscription,
} from "@/service/apis/class";
import { useGlobalStore } from "@/store/modules/global";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const global = useGlobalStore();

const props = defineProps({
  code: {
    type: String,
    default: "",
  },
});

// TAB 切換
const activeTab = ref("");

// 註冊相關
const step = ref(1); // 1: 驗證邀請碼, 2: 選擇操作, 3: 填寫表單, 4: 完成
const inviteCode = ref("");
const error = ref("");
const userData = reactive({
  username: "",
  uni_info: "",
  firstname: "",
  lastname: "",
  patternPw: [],
  image: "Villager", // 預設值
});
const registrationResult = ref(null);
const passwordConfirm = ref("");

// 訂閱相關
const subscriptionData = reactive({
  username: "",
  uniInfo: "",
  inviteCode: "",
  action: "extend", // 固定為延長訂閱
});
const subscriptionError = ref("");
const subscriptionSuccess = ref(false);

const emojiList = [
  { img: "🌳", index: "0" },
  { img: "🔑", index: "1 " },
  { img: "🔥", index: "2 " },
  { img: "🍦", index: "3 " },
  { img: "🌼", index: "4 " },
  { img: "🍭", index: "5 " },
  { img: "🎀", index: "6 " },
  { img: "📗", index: "7 " },
  { img: "🥕", index: "8 " },
  { img: "🌛", index: "9 " },
  { img: "🍎", index: "10" },
  { img: "🐘", index: "11" },
  { img: "🍔", index: "12" },
  { img: "🕜", index: "13" },
  { img: "📺", index: "14" },
  { img: "🚗", index: "15" },
  { img: "🏀", index: "16" },
  { img: "🏠", index: "17" },
  { img: "🐄", index: "18" },
  { img: "👒", index: "19" },
];
const emojiOrder = emojiList.map((e) => e.img);

const verifyCode = async () => {
  try {
    if (!inviteCode.value) {
      if (props.code) {
        inviteCode.value = props.code.trim();
      } else if (route.query.code) {
        inviteCode.value = route.query.code.trim();
      }
    }

    // 對輸入的邀請碼進行 trim 處理
    inviteCode.value = inviteCode.value.trim();

    if (!inviteCode.value) {
      error.value = t("please-enter-invitation-code");
      return;
    }
    const response = await ApiValidateInvitationCode.validate(inviteCode.value);
    console.log("response", response);
    if (response.valid && response.valid === true) {
      step.value = 2; // 進入選擇操作類型的步驟
      error.value = "";

      // 自動帶入邀請碼到訂閱展延
      subscriptionData.inviteCode = inviteCode.value;
    } else {
      error.value = response.message || t("invitation-code-invalid-or-expired");
    }
  } catch (e) {
    error.value = t("verification-failed-please-try-again-later");
    console.error("驗證邀請碼時發生錯誤:", e);
  }
};

// 選擇操作類型
function selectAction(action) {
  activeTab.value = action;
  step.value = 3; // 進入表單填寫步驟
  error.value = "";
  subscriptionError.value = "";
}

const registerUser = async () => {
  try {
    if (!validateUserData()) return;

    // 建立符合 RegisterAndJoinClassSchemas 的請求格式
    const requestData = {
      inviteCode: inviteCode.value.trim(),
      userData: {
        username: userData.username.trim(),
        uni_info: userData.uni_info.trim(),
        firstname: userData.firstname.trim(),
        lastname: userData.lastname.trim(),
        image: userData.image,
        // 確保 patternPw 保持為原始 emoji 字符
        patternPw:
          userData.patternPw && userData.patternPw.length
            ? userData.patternPw.map((emoji) => emoji)
            : [],
      },
    };

    console.log("requestData", requestData);

    // 使用 JSON 序列化和解析來檢查 emoji 是否保留
    const jsonString = JSON.stringify(requestData);
    console.log("JSON string:", jsonString);

    // 發送請求
    const response = await ApiRegisterAndJoinClass.register(requestData);

    if (response.success && response.success === true) {
      registrationResult.value = response;
      step.value = 4; // 進入成功頁面
    } else {
      // 檢查錯誤訊息是否包含重複鍵值錯誤
      if (
        response.message &&
        response.message.includes("duplicate key value")
      ) {
        // 使用 global store 顯示錯誤訊息
        global.$patch((state) => {
          state.alert.show = true;
          state.alert.message = t(
            "username-pincode-combination-already-exists"
          );
          state.alert.status = "danger";
          setTimeout(() => {
            state.alert.show = false;
          }, 3000);
        });

        // 也可以在表單下方顯示
        error.value = t("username-pincode-combination-already-exists");
      } else {
        error.value = response.message || t("registration-failed");
      }
    }
  } catch (e) {
    error.value = t("registration-failed-please-try-again-later");
    console.error("註冊時發生錯誤:", e);
  }
};

function validateUserData() {
  // 對所有輸入資料進行 trim 處理
  const trimmedUsername = userData.username.trim();
  const trimmedPassword = userData.uni_info.trim();
  const trimmedPasswordConfirm = passwordConfirm.value.trim();

  if (!trimmedUsername) {
    error.value = t("please-enter-username");
    return false;
  }
  if (!trimmedPassword) {
    error.value = t("please-enter-password");
    return false;
  }
  if (trimmedPassword !== trimmedPasswordConfirm) {
    error.value = t("passwords-do-not-match");
    return false;
  }
  if (!userData.patternPw || userData.patternPw.length < 2) {
    error.value = t("please-set-valid-graphic-password");
    return false;
  }
  error.value = "";
  return true;
}

function goToLogin() {
  router.push("/login");
}

// 返回上一頁
function goBack() {
  step.value = 2; // 返回到選擇操作類型的步驟
  activeTab.value = ""; // 清除選擇
  error.value = "";
  subscriptionError.value = "";
}

// 訂閱展延提交
async function submitSubscription() {
  try {
    subscriptionError.value = "";

    // 驗證必填欄位
    if (!subscriptionData.username.trim()) {
      subscriptionError.value = t("please-enter-username");
      return;
    }

    if (!subscriptionData.uniInfo.trim()) {
      subscriptionError.value = t("please-enter-password");
      return;
    }

    if (!subscriptionData.inviteCode || !subscriptionData.inviteCode.trim()) {
      subscriptionError.value = t("please-enter-invitation-code");
      return;
    }

    // 準備請求資料
    const requestData = {
      username: subscriptionData.username.trim(),
      uniInfo: subscriptionData.uniInfo.trim(),
      inviteCode: subscriptionData.inviteCode.trim(),
      action: "extend", // 固定為延長訂閱
    };

    console.log("訂閱請求資料:", requestData);

    // 發送請求
    const response = await ApiAddStudentSubscription.add(requestData);

    console.log("訂閱回應:", response);

    if (response.success || response.status === "success") {
      // 顯示成功訊息
      subscriptionSuccess.value = true;

      global.$patch((state) => {
        state.alert.show = true;
        state.alert.message = t("subscription-updated-successfully");
        state.alert.status = "success";
        setTimeout(() => {
          state.alert.show = false;
        }, 3000);
      });
    } else {
      subscriptionError.value = response.message || t("subscription-failed");
    }
  } catch (e) {
    subscriptionError.value = t("subscription-failed-please-try-again");
    console.error("訂閱時發生錯誤:", e);
  }
}

onMounted(() => {
  // 自動帶入邀請碼並驗證
  if (props.code) {
    inviteCode.value = props.code.trim();
    verifyCode();
  } else if (route.query.code) {
    inviteCode.value = route.query.code.trim();
    verifyCode();
  }
});
</script>

<style scoped>
/* 返回按鈕樣式 */
.back-button {
  position: absolute;
  top: 0;
  left: 0;
  background-color: transparent;
  color: #666;
  border: 1px solid #ddd;
  padding: 8px 12px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 10;
}

.back-button:hover {
  background-color: #f5f5f5;
  color: #333;
  border-color: #999;
}

.form-with-back {
  position: relative;
  padding-top: 50px;
}

.form-title-with-back {
  margin-top: 0px !important;
}

/* 操作選擇樣式 */
.action-selection {
  text-align: center;
}

.action-selection h2 {
  margin-bottom: 40px;
}

.action-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 220px;
  max-width: 280px;
  padding: 30px 20px;
  background-color: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
  color: inherit;
}

.action-btn:hover {
  border-color: #4a90e2;
  background-color: #f8fbff;
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(74, 144, 226, 0.2);
}

.action-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.action-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.action-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.bg-fixed {
  min-height: 100vh;
  width: 100vw;
  /* background: url("@/assets/bg-joinclass.png") center center/cover no-repeat
    fixed; */
  display: flex;
  align-items: center;
  justify-content: center;
}

.join-class-container.scrollable {
  max-width: 600px;
  width: 100%;
  margin: 40px auto;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.92);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  overflow-y: auto;
}

h2 {
  text-align: center;
  margin-bottom: 24px;
  color: #333;
}

.code-entry {
  text-align: center;
}

.code-entry input {
  display: block;
  width: 100%;
  max-width: 300px;
  margin: 0 auto 16px;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

button {
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

button:hover {
  background-color: #3a7bd5;
}

.error {
  color: #e74c3c;
  margin-top: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.required {
  color: #e74c3c;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.form-group select {
  cursor: pointer;
  background-color: white;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
  margin-bottom: 0;
  cursor: pointer;
}

.checkbox-group input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

.checkbox-group .hint {
  font-size: 14px;
  color: #666;
  margin: 0;
  padding-left: 28px;
}

.subscription-form {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-message {
  text-align: center;
}

.success-message h2 {
  color: #27ae60;
}

.success-message p {
  margin-bottom: 16px;
  font-size: 16px;
}

.success-message button {
  margin-top: 16px;
}

/* 強制圖形密碼四個一排 */
.pattern-container {
  grid-template-columns: repeat(4, 1fr) !important;
}
</style>
